import type { LucideIcon } from 'lucide-react';

/**
 * A segmented control — mutually exclusive choices in one tray.
 *
 * Exists because the height was wrong everywhere it was hand-rolled. The
 * pattern is a padded tray wrapping a row of buttons, so the tray renders at
 * *button height + padding*: the Support inbox had 36px buttons in a tray with
 * 4px of padding, landing at 44px next to 36px search inputs and selects. Three
 * controls, two heights, one row.
 *
 * So the height is owned here, and it is pinned to `<Button>`'s: the tray is
 * 36px — the same as `<Button size="md">`'s `min-h-[36px]` — and the segments
 * are 30px, that minus the tray's 3px padding on each side.
 *
 * Deliberately NOT taller on mobile. The guide asks for 44px touch targets and
 * a 44px tray was tried first, but `<Button size="md">` is 36px at every width,
 * so a responsive tray sat 8px taller than the button beside it on phones —
 * recreating the exact mismatch this component exists to remove. 36px is what
 * the rest of the app's controls are; raising the floor to 44 is a change to
 * `<Button>` and every control that follows it, not to one filter row.
 *
 * `rounded-xl` tray per `elevated-polish-guide.md`.
 */

export interface SegmentOption<T extends string> {
  value: T;
  /** Visible text. Omit for an icon-only segment — then `title` is required. */
  label?: string;
  icon?: LucideIcon;
  /** Badge number, e.g. how many items this segment would show. */
  count?: number;
  /** Tooltip / accessible name. Required when `label` is omitted. */
  title?: string;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: readonly SegmentOption<T>[];
  /**
   * `all` — every segment shows its label (use when there are few, short ones).
   *
   * `activeOnly` — only the selected segment shows its label; the rest collapse
   * to icon + count. This is what keeps a five-way channel switch from eating
   * 420px of a filter row. Every option still needs a `title` so the collapsed
   * segments stay identifiable by tooltip and to a screen reader.
   */
  labelMode?: 'all' | 'activeOnly';
  /** Accessible name for the group. */
  ariaLabel: string;
  className?: string;
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  labelMode = 'all',
  ariaLabel,
  className = '',
}: SegmentedControlProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`inline-flex h-9 shrink-0 items-center gap-0.5 rounded-xl bg-slate-100 p-[3px] ${className}`}
    >
      {options.map((option) => {
        const { value: optionValue, label, icon: Icon, count, title } = option;
        const isActive = optionValue === value;
        const showLabel = Boolean(label) && (labelMode === 'all' || isActive);

        return (
          <button
            key={optionValue}
            type="button"
            role="tab"
            aria-selected={isActive}
            title={title ?? label}
            onClick={() => onChange(optionValue)}
            className={`inline-flex h-[30px] items-center gap-1.5 rounded-lg px-2.5 text-sm font-semibold transition-colors ${
              isActive
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {Icon && <Icon className="h-4 w-4 shrink-0" />}
            {showLabel && <span className="whitespace-nowrap">{label}</span>}
            {count !== undefined && (
              <span
                className={`text-xs font-semibold tabular-nums ${
                  isActive ? 'text-[#007ACC]' : 'text-slate-400'
                }`}
              >
                {count}
              </span>
            )}
            {/* An icon-only segment still needs an accessible name. */}
            {!showLabel && label && <span className="sr-only">{label}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
